import SectionVisible, { useSectionVisible } from 'appContainer/SectionVisible';
import SelectGeneric from 'components/commons/form/SelectGeneric';
import { updateClaimStatus } from 'feature/claims/claimSlice';
import {
	resetInputStore,
	setInputHandleChange,
} from 'feature/input/inputSlice';
import { useClaimHook } from 'hooks/claim.hook';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { AppDispatch, RootState } from 'store/store';
import { Stack, TextFieldPopup, Typography } from 'styles';
import Button from 'styles/button/Button';
import {
	BASE_REASON_CODE_DUPLICATE,
	CLAIM_ACTIONS,
	REASON_CODES_EXTENDED,
} from 'types/Claim';
import { SECTIONS } from 'types/SectionsVisibility';
import {
	CLAIM_NOTE_MAX_LENGTH,
	REJECT_REASON_MAX_LENGTH,
	TRANSLATION_KEYS,
} from 'utils/constants';
import { useTranslation } from 'utils/translations';

const ClaimStatusChangeContent = ({
	action,
}: {
	action: CLAIM_ACTIONS.REJECT | CLAIM_ACTIONS.PENDING;
}) => {
	const { t: tClientLabels } = useTranslation('', {
		keyPrefix: TRANSLATION_KEYS.CLIENT_LABELS,
	});
	const dispatch = useDispatch<AppDispatch>();
	const { t: tDbLabels } = useTranslation('', {
		keyPrefix: TRANSLATION_KEYS.DB_LABELS,
	});
	const location = useLocation();

	const pathnames = location.pathname.split('/');
	const isClaimDetails = pathnames.at(-1) !== 'view-claims';
	const claimUUID = isClaimDetails ? pathnames.at(-1) : undefined;

	const { claimData: claim } = useSelector((store: RootState) => store.claim);
	const { selectedItems } = useSelector(
		(store: RootState) => store.tableSelectedItems,
	);

	const rejectionAdditionalFields =
		useSectionVisible(SECTIONS.ENABLE_CLAIM_REJECTION_ADDITIONAL_FIELDS, {
			customLayout: claim.customLayout,
		}) && action === CLAIM_ACTIONS.REJECT;

	useEffect(() => {
		return () => {
			dispatch(resetInputStore());
		};
	}, [dispatch]);

	const [reasonError, setReasonError] = useState<boolean>(false);

	const { reasonCode, reasonNote, denialArticle, denialNumArticle } =
		useSelector((store: RootState) => store.input);

	const { reasonCodesPending, reasonCodesReject } = useSelector(
		(store: RootState) => store.claim,
	);

	const { enableExtendedRejectionReasonCodes } = useClaimHook();

	const handleChangeSelect = (item: string) => {
		dispatch(setInputHandleChange({ name: 'reasonCode', value: item }));
		setReasonError(false);
	};

	const handleChangeNote = (note: string) => {
		dispatch(setInputHandleChange({ name: 'reasonNote', value: note }));
	};

	const handleConfirm = () => {
		const claimsToUpdate = selectedItems.map(el => el);

		if (claimsToUpdate && reasonCode && reasonNote) {
			dispatch(
				updateClaimStatus({
					claimUuids: isClaimDetails && claimUUID ? [claimUUID] : claimsToUpdate,
					statusId: action === CLAIM_ACTIONS.PENDING ? 18 : 8,
					reasonCode: reasonCode,
					note: reasonNote,
					denialArticle: denialArticle as string | undefined,
					denialNumArticle: denialNumArticle as string | undefined,
				}),
			);
		}
	};

	const getDialogSubtitle = () => {
		if (action === CLAIM_ACTIONS.PENDING) {
			return tClientLabels('dialog.pending.subtitle', {
				code: claim.externalClaimId,
			});
		}

		if (isClaimDetails) {
			return tClientLabels('dialog.reject.subtitle', {
				code: claim.externalClaimId,
			});
		} else {
			const claimExternalIDs = selectedItems.map(el => el);

			if (claimExternalIDs) {
				return selectedItems.length > 1
					? tClientLabels('dialog.rejectMultiple.subtitle', {
							codes: claimExternalIDs
								.slice(0, -1)
								.join(`, ${tClientLabels('dialog.rejectMultiple.numberSymbol')}`),
							lastCode: claimExternalIDs.at(-1),
						})
					: tClientLabels('dialog.reject.subtitle', {
							code: claimExternalIDs[0],
						});
			}
		}
	};

	const filterReasonCodes = () => {
		if (!enableExtendedRejectionReasonCodes) {
			return reasonCodesReject.filter(
				el => !REASON_CODES_EXTENDED.includes(el.code),
			);
		}

		return reasonCodesReject.filter(el => el.code !== BASE_REASON_CODE_DUPLICATE);
	};

	const createReasondCodesList = () =>
		(action === CLAIM_ACTIONS.PENDING ? reasonCodesPending : filterReasonCodes())
			.map(el => ({
				text: tDbLabels(`${el.label}`),
				value: el.code,
				id: 0,
			}))
			.sort((a, b) => a.text.localeCompare(b.text));

	return (
		<Stack minHeight={'390px'} justifyContent="space-between">
			<Stack>
				<Typography variant="body">{getDialogSubtitle()}</Typography>
				<Stack gap="40px" mt="32px" height={'100%'}>
					<SelectGeneric
						action={(payload: string) => handleChangeSelect(payload)}
						items={createReasondCodesList()}
						name={'reason'}
						initialValue={reasonCode || ''}
						error={reasonError}
						sx={{ maxWidth: '320px' }}
					/>
					{action === CLAIM_ACTIONS.PENDING ? (
						<TextFieldPopup
							label={tClientLabels('dialog.pending.argument')}
							name={'note'}
							onChange={e => handleChangeNote(e.target.value)}
							value={reasonNote || ''}
							maxLength={CLAIM_NOTE_MAX_LENGTH}
						/>
					) : (
						<>
							<SectionVisible
								sectionName={SECTIONS.ENABLE_CLAIM_REJECTION_DEFAULT_FIELDS}
								carrier={{ customLayout: claim.customLayout }}
							>
								<TextFieldPopup
									label={tClientLabels('dialog.reject.argument')}
									name={'note'}
									onChange={e => handleChangeNote(e.target.value)}
									value={reasonNote || ''}
									maxLength={CLAIM_NOTE_MAX_LENGTH}
								/>
							</SectionVisible>
							<SectionVisible
								sectionName={SECTIONS.ENABLE_CLAIM_REJECTION_ADDITIONAL_FIELDS}
								carrier={{ customLayout: claim.customLayout }}
							>
								<Stack direction="column" gap="40px">
									<TextFieldPopup
										label={tClientLabels('dialog.reject.denialReason')}
										name={'note'}
										onChange={e => handleChangeNote(e.target.value)}
										value={reasonNote || ''}
										maxLength={REJECT_REASON_MAX_LENGTH}
									/>
									<TextFieldPopup
										label={tClientLabels('dialog.reject.denialArticle')}
										name={'denialNumArticle'}
										onChange={e =>
											dispatch(
												setInputHandleChange({
													name: 'denialNumArticle',
													value: e.target.value,
												}),
											)
										}
										value={denialNumArticle || ''}
										maxLength={REJECT_REASON_MAX_LENGTH}
									/>
									<TextFieldPopup
										label={tClientLabels('dialog.reject.articleText')}
										name={'denialArticle'}
										onChange={e =>
											dispatch(
												setInputHandleChange({
													name: 'denialArticle',
													value: e.target.value,
												}),
											)
										}
										value={denialArticle || ''}
										maxLength={REJECT_REASON_MAX_LENGTH}
									/>
								</Stack>
							</SectionVisible>
						</>
					)}
				</Stack>
			</Stack>
			<Stack flexDirection={'row'} justifyContent={'flex-end'}>
				<Button
					variant="contained"
					onClick={handleConfirm}
					disabled={
						!reasonCode ||
						!reasonNote ||
						(rejectionAdditionalFields && !denialArticle) ||
						(rejectionAdditionalFields && !denialNumArticle)
					}
				>
					{action === CLAIM_ACTIONS.PENDING
						? tClientLabels('commons.buttons.moveToPending')
						: tClientLabels('commons.buttons.reject')}
				</Button>
			</Stack>
		</Stack>
	);
};

export default ClaimStatusChangeContent;
